export type HealthDeltaProps = {
  previous: number;
  current: number;
  /**
   * How far back the `previous` value was sampled. Defaults to "24h ago"
   * since that's the cadence the asset queue compares against. Override
   * when the source data uses a different window (e.g. "7d ago" for a
   * weekly trend card).
   */
  previousLabel?: string;
};

// HealthDelta is an at-a-glance "how has health moved?" annotation.
// Replaces the previous "was X · now Y" copy that didn't anchor *when*
// X was measured. Time labels are explicit ("24h ago", "Now") and the
// signed delta is colored so the direction reads instantly: red for a
// drop, green for recovery, gray for no movement.
export function HealthDelta({
  previous,
  current,
  previousLabel = '24h ago',
}: HealthDeltaProps) {
  const prev = Math.round(previous);
  const now = Math.round(current);
  const delta = now - prev;
  const tone =
    delta < 0
      ? 'text-critical'
      : delta > 0
        ? 'text-healthy-fg'
        : 'text-gray-700';
  const deltaText = delta === 0 ? '0' : delta > 0 ? `+${delta}` : `${delta}`;
  const a11yDirection =
    delta < 0
      ? 'dropped'
      : delta > 0
        ? 'recovered by'
        : 'unchanged';

  return (
    <p
      className="font-mono text-fs-meta text-gray-700"
      aria-label={`Health ${a11yDirection} ${Math.abs(delta)} points over the last day (${previousLabel} ${prev}, now ${now}).`}
    >
      <span>
        {previousLabel}: <span className="text-gray-900">{prev}</span>
      </span>
      <span className="mx-sp-2 text-gray-700">→</span>
      <span>
        Now: <span className="text-gray-900">{now}</span>
      </span>{' '}
      <span className={tone}>({deltaText})</span>
    </p>
  );
}
